import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  FileText,
  Plus,
  Settings,
  Download,
  Share2,
  Palette,
  Undo,
  Redo,
  Save,
  User,
  HelpCircle,
  Layers,
  History,
  LayoutDashboard,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const runCommand = React.useCallback((command: () => void) => {
    onOpenChange(false);
    command();
  }, [onOpenChange]);

  const quickActions = [
    {
      label: "New Whiteboard",
      icon: Plus,
      action: () => {
        // This would trigger the new whiteboard creation
        toast({ title: "Creating new whiteboard..." });
      },
      shortcut: "⌘N",
    },
    {
      label: "Save",
      icon: Save,
      action: () => {
        toast({ title: "Whiteboard saved" });
      },
      shortcut: "⌘S",
    },
    {
      label: "Undo",
      icon: Undo,
      action: () => {
        // Trigger undo action
        toast({ title: "Undone" });
      },
      shortcut: "⌘Z",
    },
    {
      label: "Redo",
      icon: Redo,
      action: () => {
        // Trigger redo action
        toast({ title: "Redone" });
      },
      shortcut: "⌘⇧Z",
    },
    {
      label: "Export as PNG",
      icon: Download,
      action: () => {
        toast({ title: "Exporting as PNG..." });
      },
      shortcut: "⌘E",
    },
    {
      label: "Share Whiteboard",
      icon: Share2,
      action: () => {
        toast({ title: "Opening share dialog..." });
      },
      shortcut: "⌘⇧S",
    },
  ];

  const navigationItems = [
    {
      label: "Go to Whiteboard",
      icon: LayoutDashboard,
      action: () => navigate("/app"),
      shortcut: "G W",
    },
    {
      label: "Go to Templates",
      icon: FileText,
      action: () => navigate("/templates"),
      shortcut: "G T",
    },
    {
      label: "Go to Recent",
      icon: History,
      action: () => navigate("/recent"),
      shortcut: "G R",
    },
    {
      label: "Go to Profile",
      icon: User,
      action: () => navigate("/profile"),
      shortcut: "G P",
    },
    {
      label: "Go to Settings",
      icon: Settings,
      action: () => navigate("/settings"),
      shortcut: "G S",
    },
  ];

  const toolsItems = [
    {
      label: "Toggle Layers Panel",
      icon: Layers,
      action: () => {
        toast({ title: "Toggling layers panel..." });
      },
    },
    {
      label: "Change Theme",
      icon: Palette,
      action: () => {
        toast({ title: "Opening theme selector..." });
      },
    },
    {
      label: "Help & Shortcuts",
      icon: HelpCircle,
      action: () => {
        toast({ title: "Opening help..." });
      },
      shortcut: "?",
    },
  ];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Quick Actions">
          {quickActions.map((item) => (
            <CommandItem
              key={item.label}
              onSelect={() => runCommand(item.action)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </div>
              {item.shortcut && (
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  {item.shortcut}
                </kbd>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
        
        <CommandSeparator />
        
        <CommandGroup heading="Navigation">
          {navigationItems.map((item) => (
            <CommandItem
              key={item.label}
              onSelect={() => runCommand(item.action)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </div>
              {item.shortcut && (
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  {item.shortcut}
                </kbd>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
        
        <CommandSeparator />
        
        <CommandGroup heading="Tools">
          {toolsItems.map((item) => (
            <CommandItem
              key={item.label}
              onSelect={() => runCommand(item.action)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </div>
              {item.shortcut && (
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  {item.shortcut}
                </kbd>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}